import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import api from '../api/client';
import { useAuth } from '../auth';
import { messageFromError } from '../components/ApiError';
import type { ApiResponse, Customer, Invoice } from '../types';

type InvoiceForm = {
  customerId: string;
  description?: string;
  currency: string;
  dueDate?: string;
  discountAmount: string;
  taxAmount: string;
  items: Array<{ description: string; quantity: string; unitAmount: string }>;
};

const initialForm: InvoiceForm = { customerId: '', currency: 'USD', discountAmount: '0', taxAmount: '0', items: [{ description: '', quantity: '1', unitAmount: '0.00' }] };

function money(amount: string | undefined, currency: string | undefined): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value); } catch { return value.toFixed(2); }
}

function formattedDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
}

function dateTimeLocal(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function invoiceForm(invoice: Invoice): InvoiceForm {
  return {
    customerId: invoice.customer.id,
    description: invoice.description ?? '',
    currency: invoice.currency,
    dueDate: dateTimeLocal(invoice.dueDate),
    discountAmount: invoice.discountAmount,
    taxAmount: invoice.taxAmount,
    items: invoice.items.map((item) => ({ description: item.description, quantity: item.quantity, unitAmount: item.unitAmount })),
  };
}

function canEdit(invoice: Invoice): boolean { return !['PAID', 'PARTIALLY_PAID', 'VOID', 'CANCELLED'].includes(invoice.status); }

export function InvoicesPage() {
  const { user } = useAuth();
  const canWrite = user?.role === 'ADMIN' || user?.permissions.some((permission) => permission.module === 'INVOICES' && permission.canWrite) === true;
  const canDelete = user?.role === 'ADMIN';
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Invoice | null>(null);
  const [linksByInvoice, setLinksByInvoice] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Invoice | null>(null);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const invoices = useQuery({ queryKey: ['invoices'], queryFn: async () => (await api.get<ApiResponse<Invoice[]>>('/invoices', { params: { pageSize: 100 } })).data });
  const customers = useQuery({ queryKey: ['customers'], queryFn: async () => (await api.get<ApiResponse<Customer[]>>('/customers', { params: { pageSize: 100 } })).data });
  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
    watch,
  } = useForm<InvoiceForm>({ defaultValues: initialForm });
  const { fields, append, remove } = useFieldArray({ control, name: 'items' });
  const saveInvoice = useMutation({
    mutationFn: ({ form, invoiceId }: { form: InvoiceForm; invoiceId?: string }) => {
      const payload = { ...form, dueDate: form.dueDate ? new Date(form.dueDate).toISOString() : undefined };
      return invoiceId ? api.patch<ApiResponse<Invoice>>(`/invoices/${invoiceId}`, payload) : api.post<ApiResponse<Invoice>>('/invoices', payload);
    },
    onSuccess: async (response, variables) => {
      if (variables.invoiceId) {
        setNotice('Invoice updated. Its prior payment link was cancelled; regenerate a new link before sending it.');
      } else {
        const generated = await api.post<ApiResponse<{ url: string }>>('/payment-links', { invoiceId: response.data.data.id });
        setLinksByInvoice((current) => ({ ...current, [response.data.data.id]: generated.data.data.url }));
      }
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setOpen(false);
      setEditing(null);
      reset(initialForm);
    },
  });
  const regenerateLink = useMutation({
    mutationFn: (invoiceId: string) => api.post<ApiResponse<{ url: string }>>('/payment-links/regenerate', { invoiceId }),
    onSuccess: (response, invoiceId) => {
      setLinksByInvoice((current) => ({ ...current, [invoiceId]: response.data.data.url }));
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
  const requestDeleteVerification = useMutation({ mutationFn: (invoiceId: string) => api.post<ApiResponse<{ verificationId: string }>>(`/invoices/${invoiceId}/delete-verification`) });
  const deleteInvoice = useMutation({
    mutationFn: ({ invoiceId, code, requestId }: { invoiceId: string; code: string; requestId: string }) => api.delete(`/invoices/${invoiceId}`, { data: { verificationId: requestId, code } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setDeleteTarget(null);
      setVerificationId(null);
      setOtpCode('');
      setDeleteError(null);
    },
  });

  const itemValues = watch('items') ?? [];
  const subtotal = itemValues.reduce((sum, item) => sum + Number(item.quantity || 0) * Number(item.unitAmount || 0), 0);
  const discount = Number(watch('discountAmount') || 0);
  const tax = Number(watch('taxAmount') || 0);
  const invoiceRows = Array.isArray(invoices.data?.data) ? invoices.data.data : [];
  const customerRows = Array.isArray(customers.data?.data) ? customers.data.data : [];
  const loadError = invoices.error ?? customers.error;
  const startCreate = () => {
    clearErrors();
    const onlyCustomer = customerRows.length === 1 ? customerRows[0] : undefined;
    reset({ ...initialForm, customerId: onlyCustomer?.id ?? '', currency: onlyCustomer?.currency ?? initialForm.currency });
    setEditing(null);
    setOpen(true);
  };
  const startEdit = (invoice: Invoice) => {
    clearErrors();
    reset(invoiceForm(invoice));
    setEditing(invoice);
    setOpen(true);
  };

  return (
    <Stack spacing={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4">Invoices</Typography>
          <Typography color="text.secondary">Create and manage payment links that recover processing fees.</Typography>
        </Box>
        {canWrite && <Button startIcon={<AddRoundedIcon />} variant="contained" disabled={!customerRows.length} onClick={startCreate}>New invoice</Button>}
      </Box>

      {notice && <Alert severity="info" onClose={() => setNotice(null)}>{notice}</Alert>}
      {loadError && <Alert severity="error">{messageFromError(loadError)}</Alert>}

      {invoices.isLoading ? <LinearProgress /> : (
        <Card>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Payment status</TableCell>
                <TableCell>Link status / expiry</TableCell>
                <TableCell>Due</TableCell>
                <TableCell align="right">Total</TableCell>
                {(canWrite || canDelete) && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {invoiceRows.map((invoice) => {
                const paymentLink = invoice.paymentLinks?.[0];
                const linkExpired = paymentLink?.expiresAt && new Date(paymentLink.expiresAt) <= new Date();
                const linkLabel = !paymentLink ? 'Not generated' : linkExpired && paymentLink.status === 'ACTIVE' ? 'Expired' : paymentLink.status;
                const generatedLink = linksByInvoice[invoice.id];
                return (
                  <TableRow key={invoice.id}>
                    <TableCell><Typography fontWeight={600}>{invoice.number}</Typography><Typography variant="caption" color="text.secondary">{invoice.description}</Typography></TableCell>
                    <TableCell>{invoice.customer?.name ?? '—'}</TableCell>
                    <TableCell><Chip label={invoice.status === 'PAID' ? 'Paid' : invoice.status.replaceAll('_', ' ')} size="small" color={invoice.status === 'PAID' ? 'success' : invoice.status === 'OPEN' ? 'warning' : 'default'} /></TableCell>
                    <TableCell>
                      {generatedLink ? <Stack direction="row" spacing={0.5} alignItems="center"><Typography component="a" href={generatedLink} target="_blank" rel="noreferrer" variant="body2" color="primary">Open payment link</Typography><Tooltip title="Copy link"><IconButton size="small" onClick={() => void navigator.clipboard.writeText(generatedLink)}><ContentCopyRoundedIcon fontSize="small" /></IconButton></Tooltip></Stack> : <Typography variant="body2">{linkLabel}</Typography>}
                      <Typography variant="caption" color="text.secondary">{paymentLink?.expiresAt ? `Expires ${formattedDate(paymentLink.expiresAt)}` : generatedLink ? 'Newly generated in this session' : 'No expiry date'}</Typography>
                    </TableCell>
                    <TableCell>{formattedDate(invoice.dueDate)}</TableCell>
                    <TableCell align="right">{money(invoice.grandTotal, invoice.currency)}</TableCell>
                    {(canWrite || canDelete) && <TableCell align="right">
                      {canWrite && canEdit(invoice) && <Tooltip title="Edit invoice"><IconButton aria-label={`Edit ${invoice.number}`} size="small" onClick={() => startEdit(invoice)}><EditRoundedIcon fontSize="small" /></IconButton></Tooltip>}
                      {canWrite && canEdit(invoice) && <Tooltip title="Regenerate payment link"><IconButton aria-label={`Regenerate payment link for ${invoice.number}`} size="small" onClick={() => void regenerateLink.mutateAsync(invoice.id).catch((error: unknown) => setNotice(messageFromError(error)))}><RefreshRoundedIcon fontSize="small" /></IconButton></Tooltip>}
                      {canDelete && canEdit(invoice) && <Tooltip title="Delete invoice"><IconButton aria-label={`Delete ${invoice.number}`} size="small" color="error" onClick={() => { setDeleteTarget(invoice); setVerificationId(null); setOtpCode(''); setDeleteError(null); }}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton></Tooltip>}
                    </TableCell>}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <Box component="form" onSubmit={handleSubmit(async (values) => {
          try { await saveInvoice.mutateAsync({ form: values, invoiceId: editing?.id }); } catch (error) { setError('root', { message: messageFromError(error) }); }
        })}>
          <DialogTitle>{editing ? `Edit ${editing.number}` : 'New invoice'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} pt={1}>
              {errors.root && <Alert severity="error">{errors.root.message}</Alert>}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Controller
                  control={control}
                  name="customerId"
                  rules={{ required: 'Customer is required' }}
                  render={({ field }) => <TextField select label="Customer" fullWidth value={field.value ?? ''} error={Boolean(errors.customerId)} helperText={errors.customerId?.message} onChange={(event) => { field.onChange(event); const customer = customerRows.find((item) => item.id === event.target.value); if (customer) setValue('currency', customer.currency, { shouldDirty: true, shouldValidate: true }); }}>
                    {customerRows.map((customer) => <MenuItem key={customer.id} value={customer.id}>{customer.name} · {customer.email}</MenuItem>)}
                  </TextField>}
                />
                <Controller
                  control={control}
                  name="currency"
                  render={({ field }) => <TextField select label="Currency" fullWidth value={field.value ?? 'USD'} onChange={field.onChange}><MenuItem value="USD">USD</MenuItem><MenuItem value="AUD">AUD</MenuItem><MenuItem value="GBP">GBP</MenuItem><MenuItem value="EUR">EUR</MenuItem><MenuItem value="CAD">CAD</MenuItem></TextField>}
                />
                <TextField label="Due date" type="datetime-local" InputLabelProps={{ shrink: true }} fullWidth {...register('dueDate')} />
              </Stack>
              <TextField label="Description" {...register('description')} />
              <Divider />
              <Typography variant="subtitle2">Line items</Typography>
              {fields.map((field, index) => <Stack key={field.id} direction={{ xs: 'column', sm: 'row' }} spacing={1}><TextField label="Description" fullWidth {...register(`items.${index}.description`, { required: 'Item description is required' })} /><TextField label="Qty" type="number" inputProps={{ min: 0.001, step: 0.001 }} sx={{ width: { sm: 130 } }} {...register(`items.${index}.quantity`, { required: true })} /><TextField label="Unit amount" type="number" inputProps={{ min: 0.01, step: 0.01 }} sx={{ width: { sm: 160 } }} {...register(`items.${index}.unitAmount`, { required: true })} /><Button color="error" disabled={fields.length === 1} onClick={() => remove(index)}>Remove</Button></Stack>)}
              <Button onClick={() => append({ description: '', quantity: '1', unitAmount: '0.00' })}>Add item</Button>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}><TextField label="Discount" type="number" inputProps={{ min: 0, step: 0.01 }} fullWidth {...register('discountAmount')} /><TextField label="Tax" type="number" inputProps={{ min: 0, step: 0.01 }} fullWidth {...register('taxAmount')} /></Stack>
              <Card variant="outlined"><CardContent><Typography variant="body2" color="text.secondary">Invoice amount before processing fee</Typography><Typography variant="h6">{money(Math.max(0, subtotal - discount + tax).toFixed(2), watch('currency'))}</Typography><Typography variant="caption" color="text.secondary">The API securely recalculates the configured fee when you save.</Typography></CardContent></Card>
            </Stack>
          </DialogContent>
          <DialogActions><Button onClick={() => setOpen(false)}>Cancel</Button><Button type="submit" variant="contained" disabled={isSubmitting || saveInvoice.isPending}>{editing ? 'Save changes' : 'Create invoice & link'}</Button></DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => { if (!deleteInvoice.isPending) setDeleteTarget(null); }} fullWidth maxWidth="xs">
        <DialogTitle>Delete invoice?</DialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={1}>
            <Typography>Deleting {deleteTarget?.number} cancels its active payment link. A six-digit code must be approved by the super administrator.</Typography>
            {deleteError && <Alert severity="error">{deleteError}</Alert>}
            {!verificationId ? <Button variant="outlined" disabled={requestDeleteVerification.isPending} onClick={async () => {
              if (!deleteTarget) return;
              try { const response = await requestDeleteVerification.mutateAsync(deleteTarget.id); setVerificationId(response.data.data.verificationId); } catch (error) { setDeleteError(messageFromError(error)); }
            }}>Send approval code</Button> : <TextField label="Approval code" value={otpCode} onChange={(event) => setOtpCode(event.target.value.replace(/\D/g, '').slice(0, 6))} inputProps={{ inputMode: 'numeric', maxLength: 6 }} autoFocus />}
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setDeleteTarget(null)} disabled={deleteInvoice.isPending}>Cancel</Button><Button color="error" variant="contained" disabled={!verificationId || otpCode.length !== 6 || deleteInvoice.isPending} onClick={async () => {
          if (!deleteTarget || !verificationId) return;
          try { await deleteInvoice.mutateAsync({ invoiceId: deleteTarget.id, requestId: verificationId, code: otpCode }); } catch (error) { setDeleteError(messageFromError(error)); }
        }}>Delete invoice</Button></DialogActions>
      </Dialog>
    </Stack>
  );
}
